import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useAccount, TAB_DEFS, ACTION_DEFS, FEATURE_DEFS, defaultTeamPermissions, fullTeamPermissions } from '@/contexts/AccountContext';
import { UserPlus, Trash2, Crown, Copy, Check, Mail, Send, KeyRound, ArrowLeftRight, ShieldCheck, Clock, UserCheck, X } from 'lucide-react';
import InlineEdit from '@/components/InlineEdit';

const AVATAR_TONES = ['#C9E0E0', '#D4C9E0', '#E0D9C9', '#C9D4E0'];
const toneFor = (name) => {
  const s = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_TONES[s % AVATAR_TONES.length];
};
const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const apiErr = (e) => e?.response?.data?.detail || 'Something went wrong';

export default function TeamPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const { account, updateUserPermissions, updateOwnerProfile, transferOwnership } = useAccount();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberTasks, setMemberTasks] = useState([]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [tempCred, setTempCred] = useState(null);
  const [tgLinks, setTgLinks] = useState({});
  const [permUser, setPermUser] = useState(null);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [pending, setPending] = useState([]);

  const loadPending = async () => {
    try { const r = await api.get('/users/pending'); setPending(r.data || []); } catch { /* non-owner */ }
  };

  const loadMembers = async () => {
    try {
      const res = await api.get('/team');
      setMembers(res.data);
      setSelectedMember(prev => prev ? res.data.find(m => m.id === prev.id) || null : null);
    } catch { toast.error('Failed to load team'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadMembers(); }, []);
  useEffect(() => {
    if (isOwner) {
      api.get('/telegram/links').then(r => setTgLinks(r.data || {})).catch(() => {});
      loadPending();
    }
  }, [isOwner]);

  const approvePending = async (m) => {
    try {
      await api.put(`/users/${m.id}/approve`);
      await Promise.all([loadPending(), loadMembers()]);
      toast.success(`${m.name} approved — they now have access`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const declinePending = async (m) => {
    try {
      await api.delete(`/users/${m.id}`);
      await loadPending();
      toast(`Declined ${m.name}'s access request`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const memberId = searchParams.get('member');
    if (memberId && members.length) {
      const found = members.find(m => m.id === memberId);
      if (found) {
        selectMember(found);
        const next = new URLSearchParams(searchParams);
        next.delete('member');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, members]);

  const ownerMember = members.find(m => m.is_owner);

  const selectMember = async (member) => {
    setSelectedMember(member);
    try {
      const res = await api.get(`/tasks?assigned_to=${member.id}`);
      setMemberTasks(res.data);
    } catch { setMemberTasks([]); }
  };

  const updateMemberField = async (field, value) => {
    if (!selectedMember) return;
    try {
      await api.put(`/team/${selectedMember.id}`, { [field]: value });
      setSelectedMember(prev => ({ ...prev, [field]: value }));
      setMembers(prev => prev.map(m => m.id === selectedMember.id ? { ...m, [field]: value } : m));
    } catch { toast.error('Update failed'); }
  };

  const handleRemoveMember = async (memberId) => {
    const member = members.find(m => m.id === memberId);
    try {
      await api.delete(`/users/${memberId}`);
      await loadMembers();
      if (selectedMember?.id === memberId) { setSelectedMember(null); setMemberTasks([]); }
      setMemberToRemove(null);
      toast(`${member?.name || 'Member'} has been removed.`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const savePermissions = async (perms) => {
    try {
      await updateUserPermissions(permUser.id, perms);
      await loadMembers();
      setPermUser(null);
      toast.success('Permissions updated');
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleFullAccess = async (member, full) => {
    try {
      await updateUserPermissions(member.id, full ? fullTeamPermissions() : defaultTeamPermissions());
      await loadMembers();
      toast.success(full ? 'Granted full access' : 'Set to limited access');
    } catch (e) { toast.error(apiErr(e)); }
  };

  const canRemove = (m) => isOwner && !m.is_owner && m.id !== user?.id;

  const workloadPct = (tasks) => Math.min(100, (tasks / 10) * 100);
  const workloadColor = (tasks) => tasks > 7 ? '#C4727F' : tasks > 4 ? '#C67D5B' : '#7DA68A';
  const accessLabel = (m) => m.is_owner ? 'Full access' : (m.permissions?.features?.manageData && m.permissions?.features?.financials ? 'Full access' : 'Limited access');

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#3A8C8C] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="team-page">
      <div className="section-heading-row flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-['Manrope'] font-bold">Team &amp; Access</h1>
          <p className="text-sm text-[#6B7280] mt-0.5" data-testid="team-count">{members.length} members · manage roles &amp; permissions</p>
        </div>
        {isOwner && (
          <Button onClick={() => setShowAddModal(true)} className="h-9 bg-[#3A8C8C] hover:bg-[#2E7A7A] text-white rounded-lg" data-testid="add-member-btn">
            <UserPlus size={16} strokeWidth={1.5} className="mr-2" /> Add Member
          </Button>
        )}
      </div>

      {/* Owner settings card */}
      {isOwner && ownerMember && (
        <div className="glass-card rounded-2xl p-5" data-testid="owner-settings-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#3A8C8C]/10 flex items-center justify-center">
                <Crown size={20} strokeWidth={1.5} className="text-[#3A8C8C]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#2C2C2E]">{ownerMember.name} <span className="text-xs text-[#6B7280] font-normal">(You — Owner)</span></p>
                <p className="text-xs text-[#6B7280]">{ownerMember.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="rounded-xl border-[#E8E6E1]" onClick={() => setOwnerOpen(true)} data-testid="edit-owner-btn">Edit Profile</Button>
              <Button variant="outline" className="rounded-xl border-[#C4727F]/30 text-[#C4727F] hover:bg-[#C4727F]/5" onClick={() => setTransferOpen(true)} data-testid="transfer-ownership-btn">
                <ArrowLeftRight size={15} strokeWidth={1.5} className="mr-2" /> Transfer Ownership
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pending access requests (owner approval queue) */}
      {isOwner && pending.length > 0 && (
        <div className="glass-card rounded-2xl p-5 border border-[#C67D5B]/30 bg-[#C67D5B]/[0.03]" data-testid="pending-requests-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#C67D5B]/10 flex items-center justify-center">
              <Clock size={18} strokeWidth={1.5} className="text-[#C67D5B]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2C2C2E]">Pending Access Requests</p>
              <p className="text-[11px] text-[#6B7280]">{pending.length} {pending.length === 1 ? 'person' : 'people'} signed in with Google and need your approval</p>
            </div>
          </div>
          <div className="space-y-2">
            {pending.map(p => (
              <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-[#E8E6E1]" data-testid={`pending-${p.id}`}>
                <Avatar className="w-9 h-9">
                  <AvatarImage src={p.avatar || undefined} />
                  <AvatarFallback style={{ backgroundColor: toneFor(p.name), color: '#2C2C2E' }} className="text-xs font-semibold">{getInitials(p.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2C2C2E] truncate">{p.name}</p>
                  <p className="text-xs text-[#6B7280] truncate">{p.email}</p>
                </div>
                <Button size="sm" className="rounded-lg bg-[#3A8C8C] hover:bg-[#2E7A7A] text-white h-8" onClick={() => approvePending(p)} data-testid={`approve-${p.id}`}>
                  <UserCheck size={14} strokeWidth={1.5} className="mr-1.5" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="rounded-lg border-[#E8E6E1] text-[#C4727F] hover:bg-[#C4727F]/5 h-8" onClick={() => declinePending(p)} data-testid={`decline-${p.id}`}>
                  <X size={14} strokeWidth={1.5} className="mr-1.5" /> Decline
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Member list */}
        <div className="space-y-3" data-testid="team-list">
          {members.map(m => (
            <div
              key={m.id}
              onClick={() => selectMember(m)}
              className={`group relative glass-card rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all duration-300 ${selectedMember?.id === m.id ? 'ring-2 ring-[#3A8C8C]/30' : ''}`}
              data-testid={`team-member-${m.id}`}
            >
              {canRemove(m) && (
                <button
                  onClick={(e) => { e.stopPropagation(); setMemberToRemove(m); }}
                  className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 p-1 rounded-md text-[#6B6B6B] hover:text-[#D94F4F] transition-all"
                  title="Remove member"
                  data-testid={`remove-icon-${m.id}`}
                >
                  <Trash2 size={16} strokeWidth={1.5} />
                </button>
              )}
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={m.avatar || undefined} />
                  <AvatarFallback style={{ backgroundColor: toneFor(m.name), color: '#2C2C2E' }} className="text-sm font-semibold">{getInitials(m.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#2C2C2E] flex items-center gap-1.5">
                    {m.name}
                    {m.is_owner && <Crown size={12} strokeWidth={1.5} className="text-[#3A8C8C]" />}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Badge className={`text-[9px] capitalize ${m.is_owner ? 'bg-[#3A8C8C]/10 text-[#3A8C8C]' : 'bg-[#E8E6E1] text-[#6B7280]'}`} data-testid={`role-badge-${m.id}`}>{m.is_owner ? 'owner' : 'team'}</Badge>
                    {m.status === 'invited' && <Badge className="text-[9px] bg-[#C67D5B]/10 text-[#C67D5B]">invited</Badge>}
                  </div>
                </div>
                <Badge className="bg-[#E8E6E1] text-[#2C2C2E] text-[10px]">{m.active_tasks || 0} tasks</Badge>
              </div>
              {isOwner && (
                <div className="mt-2">
                  <Badge className={tgLinks[m.id] ? 'bg-[#3A8C8C]/10 text-[#3A8C8C] text-[10px]' : 'bg-[#E8E6E1] text-[#6B7280] text-[10px]'} data-testid={`tg-status-${m.id}`}>
                    <Send size={9} strokeWidth={1.5} className="mr-1" /> {tgLinks[m.id] ? 'Telegram Linked ✓' : 'Not linked'}
                  </Badge>
                </div>
              )}
              <div className="mt-3">
                <div className="flex items-center justify-between text-[10px] text-[#6B7280] mb-1">
                  <span>Workload</span>
                  <span>{Math.round(workloadPct(m.active_tasks || 0))}%</span>
                </div>
                <div className="h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${workloadPct(m.active_tasks || 0)}%`, backgroundColor: workloadColor(m.active_tasks || 0) }} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Member detail */}
        <div className="lg:col-span-2">
          {selectedMember ? (
            <div className="glass-card rounded-2xl p-6 animate-fade-in" data-testid="member-detail">
              <div className="flex items-start gap-4 mb-6">
                <Avatar className="w-14 h-14">
                  <AvatarImage src={selectedMember.avatar || undefined} />
                  <AvatarFallback style={{ backgroundColor: toneFor(selectedMember.name), color: '#2C2C2E' }} className="text-lg font-semibold">{getInitials(selectedMember.name)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  {isOwner && !selectedMember.is_owner ? (
                    <InlineEdit value={selectedMember.name} onSave={v => updateMemberField('name', v)} className="text-xl font-['Manrope'] font-bold text-[#2C2C2E]" testId="inline-member-name" />
                  ) : (
                    <p className="text-xl font-['Manrope'] font-bold text-[#2C2C2E]">{selectedMember.name}</p>
                  )}
                  <p className="text-sm text-[#6B7280] capitalize flex items-center gap-1.5">
                    {selectedMember.is_owner ? <><Crown size={13} strokeWidth={1.5} className="text-[#3A8C8C]" /> owner · full access</> : `team · ${accessLabel(selectedMember).toLowerCase()}`}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-[#6B7280]">
                    {isOwner && !selectedMember.is_owner ? (
                      <>
                        <InlineEdit value={selectedMember.email} onSave={v => updateMemberField('email', v)} className="text-xs text-[#6B7280]" placeholder="Add email" testId="inline-member-email" />
                        <InlineEdit value={selectedMember.phone} onSave={v => updateMemberField('phone', v)} className="text-xs text-[#6B7280]" placeholder="Add phone" testId="inline-member-phone" />
                      </>
                    ) : (
                      <>
                        <span>{selectedMember.email}</span>
                        {selectedMember.phone && <span>{selectedMember.phone}</span>}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Access & Permissions (owner managing a team member) */}
              {isOwner && !selectedMember.is_owner && (
                <div className="mb-6 p-4 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]" data-testid="member-access-panel">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck size={16} strokeWidth={1.5} className="text-[#3A8C8C]" />
                      <div>
                        <p className="text-sm font-semibold text-[#2C2C2E]">Access &amp; Permissions</p>
                        <p className="text-[11px] text-[#6B7280]">{accessLabel(selectedMember)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 text-xs text-[#6B7280]">
                        Full access
                        <Switch
                          checked={!!(selectedMember.permissions?.features?.manageData && selectedMember.permissions?.features?.financials)}
                          onCheckedChange={(v) => toggleFullAccess(selectedMember, v)}
                          data-testid={`full-access-toggle-${selectedMember.id}`}
                        />
                      </label>
                      <Button variant="outline" size="sm" className="rounded-lg border-[#E8E6E1] text-[#3A8C8C]" onClick={() => setPermUser(selectedMember)} data-testid={`edit-perms-${selectedMember.id}`}>
                        <KeyRound size={14} strokeWidth={1.5} className="mr-1.5" /> Edit Permissions
                      </Button>
                      {selectedMember.status === 'invited' && (
                        <Button variant="ghost" size="sm" className="rounded-lg text-[#6B7280]" title="Resend invite"
                          onClick={async () => { try { const r = await api.post(`/users/${selectedMember.id}/resend-invite`); setTempCred({ email: selectedMember.email, password: r.data.temp_password }); } catch (e) { toast.error(apiErr(e)); } }}
                          data-testid={`resend-${selectedMember.id}`}>
                          <Send size={14} strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <h3 className="section-heading text-sm font-semibold font-['Manrope'] mb-3">Assigned Tasks ({memberTasks.length})</h3>
              <div className="space-y-2">
                {memberTasks.map(task => {
                  const isOverdue = task.due_date && new Date(task.due_date) < Date.now() && task.status !== 'Completed';
                  return (
                    <div key={task.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#FAFAF8] border border-[#E8E6E1]">
                      <div className={`w-2 h-2 rounded-full ${task.priority === 'High' ? 'bg-[#C4727F]' : task.priority === 'Medium' ? 'bg-[#C67D5B]' : 'bg-[#7DA68A]'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#2C2C2E]">{task.title}</p>
                        <p className="text-[10px] text-[#6B7280]">{task.project_name}</p>
                      </div>
                      <Badge className={`text-[10px] ${task.status === 'Completed' ? 'bg-[#7DA68A]/10 text-[#7DA68A]' : isOverdue ? 'bg-[#C4727F]/10 text-[#C4727F]' : 'bg-[#E8E6E1] text-[#2C2C2E]'}`}>{task.status}</Badge>
                    </div>
                  );
                })}
                {memberTasks.length === 0 && <p className="text-sm text-[#6B7280] text-center py-6">No tasks assigned</p>}
              </div>

              {canRemove(selectedMember) && (
                <div className="mt-6 pt-4 border-t border-[#E8E6E1]">
                  <button onClick={() => setMemberToRemove(selectedMember)} className="text-[13px] font-medium text-[#D94F4F] hover:underline" data-testid="remove-member-detail-btn">
                    Remove Member
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="glass-card rounded-2xl p-12 flex items-center justify-center">
              <p className="text-sm text-[#6B7280]">Select a team member to view details</p>
            </div>
          )}
        </div>
      </div>

      <AddMemberModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        existingEmails={members.map(m => (m.email || '').toLowerCase())}
        onAdded={(email, password) => { loadMembers(); setTempCred({ email, password }); }}
      />

      {permUser && (
        <PermissionsDialog user={permUser} onClose={() => setPermUser(null)} onSave={savePermissions} />
      )}

      <OwnerProfileDialog open={ownerOpen} onClose={() => setOwnerOpen(false)} owner={ownerMember}
        onSave={async (d) => { try { await updateOwnerProfile(d); await loadMembers(); setOwnerOpen(false); toast.success('Profile updated'); } catch (e) { toast.error(apiErr(e)); } }} />

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        candidates={members.filter(m => !m.is_owner && m.status !== 'invited')}
        onTransfer={async (newOwnerId) => { try { await transferOwnership(newOwnerId); toast.success('Ownership transferred'); window.location.href = '/'; } catch (e) { toast.error(apiErr(e)); } }}
      />

      <Dialog open={!!memberToRemove} onOpenChange={(o) => !o && setMemberToRemove(null)}>
        <DialogContent className="rounded-2xl max-w-[420px]" data-testid="remove-member-modal">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Remove Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <span className="font-semibold text-[#2C2C2E]">{memberToRemove?.name}</span> from the team? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-[#E5E5E3]" onClick={() => setMemberToRemove(null)}>Cancel</Button>
            <Button className="rounded-lg bg-[#D94F4F] hover:bg-[#c43f3f] text-white" onClick={() => handleRemoveMember(memberToRemove.id)} data-testid="confirm-remove-btn">Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TempPasswordDialog cred={tempCred} onClose={() => setTempCred(null)} />
    </div>
  );
}

function AddMemberModal({ open, onClose, existingEmails, onAdded }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'team', phone: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const reset = () => { setForm({ name: '', email: '', role: 'team', phone: '' }); setErrors({}); };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    else if (!emailValid(form.email.trim())) errs.email = 'Enter a valid email address.';
    else if (existingEmails.includes(form.email.trim().toLowerCase())) errs.email = 'A member with this email already exists.';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      const res = await api.post('/users/invite', {
        name: form.name.trim(), email: form.email.trim(), role: form.role,
      });
      if (form.phone.trim() && res.data?.user?.id) {
        try { await api.put(`/team/${res.data.user.id}`, { phone: form.phone.trim() }); } catch { /* non-blocking */ }
      }
      toast.success('Member added successfully');
      const email = form.email.trim();
      const password = res.data.temp_password;
      reset();
      onClose();
      onAdded(email, password);
    } catch (e) {
      const detail = apiErr(e);
      if (/already/i.test(detail)) setErrors({ email: 'A member with this email already exists.' });
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-2xl max-w-[480px]" data-testid="add-member-modal">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2"><Mail size={18} strokeWidth={1.5} className="text-[#3A8C8C]" /> Add Team Member</DialogTitle>
          <DialogDescription>Creates a login instantly. A temporary password is shown after adding.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs text-[#6B6B6B]">Full Name</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Riya Shah" className="mt-1 rounded-md border-[#E5E5E3]" data-testid="add-member-name" />
            {errors.name && <p className="text-xs text-[#D94F4F] mt-1" data-testid="error-name">{errors.name}</p>}
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Email Address</Label>
            <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="e.g. riya@interiorstory.in" className="mt-1 rounded-md border-[#E5E5E3]" data-testid="add-member-email" />
            {errors.email && <p className="text-xs text-[#D94F4F] mt-1" data-testid="error-email">{errors.email}</p>}
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Access Level</Label>
            <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1 rounded-md border-[#E5E5E3]" data-testid="add-member-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="team">Team — custom permissions</SelectItem>
                <SelectItem value="full">Team — full access</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#9CA3AF] mt-1">You can fine-tune permissions anytime from the member's detail panel.</p>
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Phone Number <span className="text-[#9CA3AF]">(optional)</span></Label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="e.g. +91 98765 43210" className="mt-1 rounded-md border-[#E5E5E3]" data-testid="add-member-phone" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-lg border-[#E5E5E3]" onClick={close}>Cancel</Button>
          <Button disabled={busy} className="rounded-lg bg-[#3A8C8C] hover:bg-[#2E7A7A] text-white" onClick={submit} data-testid="submit-add-member">{busy ? 'Adding…' : 'Add Member'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PermissionsDialog({ user, onClose, onSave }) {
  const [perms, setPerms] = useState(() => {
    const base = defaultTeamPermissions();
    const p = user.permissions || {};
    return {
      tabs: { ...base.tabs, ...(p.tabs || {}) },
      actions: { ...base.actions, ...(p.actions || {}) },
      features: { ...base.features, ...(p.features || {}) },
    };
  });
  const [busy, setBusy] = useState(false);
  const toggleTab = (key) => setPerms(p => ({ ...p, tabs: { ...p.tabs, [key]: !p.tabs[key] } }));
  const toggleAction = (key) => setPerms(p => ({ ...p, actions: { ...p.actions, [key]: !p.actions[key] } }));
  const toggleFeature = (key) => setPerms(p => ({ ...p, features: { ...p.features, [key]: !p.features[key] } }));
  const save = async () => { setBusy(true); try { await onSave(perms); } finally { setBusy(false); } };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Permissions — {user.name}</DialogTitle>
          <DialogDescription>Control which project tabs, actions and features this team member can access.</DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <p className="text-xs font-semibold text-[#2C2C2E] uppercase tracking-wide mb-2">Feature Access</p>
          <div className="space-y-1">
            {FEATURE_DEFS.map(f => (
              <div key={f.key} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-[#2C2C2E]">{f.label}</span>
                <Switch checked={!!perms.features?.[f.key]} onCheckedChange={() => toggleFeature(f.key)} data-testid={`perm-feature-${f.key}`} />
              </div>
            ))}
          </div>
          <p className="text-xs font-semibold text-[#2C2C2E] uppercase tracking-wide mt-4 mb-2">Actions</p>
          <div className="space-y-1">
            {ACTION_DEFS.map(a => (
              <div key={a.key} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-[#2C2C2E]">{a.label}</span>
                <Switch checked={!!perms.actions?.[a.key]} onCheckedChange={() => toggleAction(a.key)} data-testid={`perm-action-${a.key}`} />
              </div>
            ))}
          </div>
          <p className="text-xs font-semibold text-[#2C2C2E] uppercase tracking-wide mt-4 mb-2">Project Tabs</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {TAB_DEFS.map(t => (
              <div key={t.key} className="flex items-center justify-between py-1.5">
                <span className="text-sm text-[#2C2C2E]">{t.label}</span>
                <Switch checked={!!perms.tabs?.[t.key]} onCheckedChange={() => toggleTab(t.key)} data-testid={`perm-tab-${t.key}`} />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl border-[#E8E6E1]" onClick={onClose}>Cancel</Button>
          <Button disabled={busy} className="bg-[#3A8C8C] hover:bg-[#327c7c] text-white rounded-xl" onClick={save} data-testid="perms-save">{busy ? 'Saving…' : 'Save Permissions'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OwnerProfileDialog({ open, onClose, owner, onSave }) {
  const [name, setName] = useState(owner?.name || '');
  const [email, setEmail] = useState(owner?.email || '');
  useEffect(() => { setName(owner?.name || ''); setEmail(owner?.email || ''); }, [owner]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl" data-testid="owner-profile-dialog">
        <DialogHeader><DialogTitle className="font-['Manrope']">Edit Owner Profile</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs text-[#6B7280]">Full Name</Label><Input value={name} onChange={e => setName(e.target.value)} className="mt-1 rounded-xl border-[#E8E6E1]" data-testid="owner-name" /></div>
          <div><Label className="text-xs text-[#6B7280]">Email</Label><Input value={email} onChange={e => setEmail(e.target.value)} className="mt-1 rounded-xl border-[#E8E6E1]" data-testid="owner-email" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl border-[#E8E6E1]" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#3A8C8C] hover:bg-[#327c7c] text-white rounded-xl" onClick={() => onSave({ name, email })} data-testid="owner-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransferDialog({ open, onClose, candidates, onTransfer }) {
  const [newOwnerId, setNewOwnerId] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const canTransfer = newOwnerId && confirmText === 'CONFIRM';
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setNewOwnerId(''); setConfirmText(''); } onClose(); }}>
      <DialogContent className="rounded-2xl" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] text-[#C4727F] flex items-center gap-2"><ArrowLeftRight size={18} strokeWidth={1.5} /> Transfer Ownership</DialogTitle>
          <DialogDescription>This makes another team member the Owner. You'll become a Team member (with full access) and lose owner-only controls. Irreversible by you.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs text-[#6B7280]">New Owner</Label>
            <Select value={newOwnerId} onValueChange={setNewOwnerId}>
              <SelectTrigger className="mt-1 rounded-xl border-[#E8E6E1]" data-testid="transfer-select"><SelectValue placeholder="Select a member" /></SelectTrigger>
              <SelectContent>
                {candidates.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>)}
              </SelectContent>
            </Select>
            {candidates.length === 0 && <p className="text-xs text-[#C4727F] mt-1">No eligible active members to transfer to.</p>}
          </div>
          <div>
            <Label className="text-xs text-[#6B7280]">Type <span className="font-bold text-[#2C2C2E]">CONFIRM</span> to proceed</Label>
            <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="CONFIRM" className="mt-1 rounded-xl border-[#E8E6E1]" data-testid="transfer-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl border-[#E8E6E1]" onClick={onClose}>Cancel</Button>
          <Button disabled={!canTransfer} className="bg-[#C4727F] hover:bg-[#b3636f] text-white rounded-xl disabled:opacity-40" onClick={() => onTransfer(newOwnerId)} data-testid="transfer-submit">Transfer Ownership</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TempPasswordDialog({ cred, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!cred) return null;
  const copy = () => { navigator.clipboard?.writeText(cred.password); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <Dialog open={!!cred} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl max-w-[440px]" data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Temporary Password</DialogTitle>
          <DialogDescription>Share these credentials with <b>{cred.email}</b>. They can change it later via "Forgot password".</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 bg-[#FAFAF8] border border-[#E5E5E3] rounded-xl px-4 py-3 my-2">
          <code className="text-base font-mono text-[#2C2C2E]" data-testid="temp-password-value">{cred.password}</code>
          <Button variant="ghost" size="sm" onClick={copy} data-testid="copy-temp-password">{copied ? <Check size={16} className="text-[#7DA68A]" /> : <Copy size={16} />}</Button>
        </div>
        <DialogFooter><Button className="rounded-lg bg-[#3A8C8C] hover:bg-[#2E7A7A] text-white" onClick={onClose}>Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
